<template>
  <Teleport to="body">
    <div
      v-if="canShowAgenticButton && showFloating"
      ref="launcherRef"
      class="agentic-btn-launcher"
      :class="{
        'agentic-btn-launcher--above-footer':
          agenticButtonAboveFooter && !draggedPosition,
        'agentic-btn-launcher--dragging': isDragging,
        'agentic-btn-launcher--dragged': !!draggedPosition,
      }"
      :style="launcherStyle"
      @pointerdown="onPointerDown">
      <AgenticButton compact :aria-label="ariaLabel" @click="onButtonClick" />
    </div>
  </Teleport>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import AgenticButton from '@/components/AgenticButton.vue';
import { useGoToAgent } from '@/composables/useGoToAgent';

const route = useRoute();
const { t } = useI18n();
const { canShowAgenticButton, goToAgent, agenticButtonAboveFooter } =
  useGoToAgent();

const showFloating = computed(() => route.name !== 'home');

const ariaLabel = computed(() => t('general.agent.title'));

// --- Drag support ---------------------------------------------------------
const STORAGE_KEY = 'gc_agentic_btn_pos';
const DRAG_THRESHOLD = 6; // px before a press becomes a drag
const EDGE_MARGIN = 8; // keep the button this far from the viewport edge

const launcherRef = ref(null);
const isDragging = ref(false);
// Persisted across remounts (e.g. route changes) within the session.
const draggedPosition = ref(loadSavedPosition());

let activePointerId = null;
let capturedEl = null;
let didDrag = false;
let suppressClick = false;
let suppressClickTimer = null;
let startX = 0;
let startY = 0;
let originLeft = 0;
let originTop = 0;

const launcherStyle = computed(() => {
  if (!draggedPosition.value) return {};
  return {
    // Whole pixels only: fractional offsets make the button's mask layers
    // rasterize with the right edge clipped (visible in the hover state).
    left: `${Math.round(draggedPosition.value.left)}px`,
    top: `${Math.round(draggedPosition.value.top)}px`,
    right: 'auto',
    bottom: 'auto',
  };
});

function loadSavedPosition() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.left === 'number' && typeof parsed?.top === 'number') {
      return parsed;
    }
  } catch {
    // ignore malformed/unavailable storage
  }
  return null;
}

function savePosition(pos) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
  } catch {
    // ignore storage write failures (private mode, quota, etc.)
  }
}

function clamp(value, min, max) {
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
}

function clampToViewport(left, top) {
  const el = launcherRef.value;
  const w = el?.offsetWidth ?? 0;
  const h = el?.offsetHeight ?? 0;
  return {
    left: Math.round(
      clamp(left, EDGE_MARGIN, window.innerWidth - w - EDGE_MARGIN),
    ),
    top: Math.round(
      clamp(top, EDGE_MARGIN, window.innerHeight - h - EDGE_MARGIN),
    ),
  };
}

// Guarantee the whole button stays on screen. If any part is outside the
// viewport, snap it to the nearest in-bounds position. Returns true when it
// had to move the button.
function ensureInViewport() {
  if (!draggedPosition.value) return false;
  const clamped = clampToViewport(
    draggedPosition.value.left,
    draggedPosition.value.top,
  );
  if (
    clamped.left !== draggedPosition.value.left ||
    clamped.top !== draggedPosition.value.top
  ) {
    draggedPosition.value = clamped;
    return true;
  }
  return false;
}

function onPointerDown(e) {
  // Primary button / touch / pen only
  if (e.button != null && e.button !== 0) return;
  const btn = e.target.closest?.('.melissa-btn');
  const launcher = launcherRef.value;
  if (!btn || !launcher) return;

  // Start every gesture from a clean slate. iOS Safari does NOT emit a
  // `click` after a drag, so we can't rely on onButtonClick to clear these
  // flags — otherwise they'd leak into and swallow the next genuine tap
  // (the "have to double-tap after dragging" bug).
  if (suppressClickTimer !== null) {
    clearTimeout(suppressClickTimer);
    suppressClickTimer = null;
  }
  suppressClick = false;
  activePointerId = e.pointerId;
  didDrag = false;
  startX = e.clientX;
  startY = e.clientY;

  const rect = launcher.getBoundingClientRect();
  originLeft = rect.left;
  originTop = rect.top;

  try {
    btn.setPointerCapture(e.pointerId);
  } catch {
    // capture is best-effort; drag still works via bubbled events
  }
  capturedEl = btn;
  btn.addEventListener('pointermove', onPointerMove);
  btn.addEventListener('pointerup', onPointerUp);
  btn.addEventListener('pointercancel', onPointerUp);
}

function onPointerMove(e) {
  if (e.pointerId !== activePointerId) return;
  const dx = e.clientX - startX;
  const dy = e.clientY - startY;

  if (!didDrag && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;

  didDrag = true;
  isDragging.value = true;
  draggedPosition.value = clampToViewport(originLeft + dx, originTop + dy);
}

function onPointerUp(e) {
  if (e.pointerId !== activePointerId) return;

  if (capturedEl) {
    try {
      capturedEl.releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
    capturedEl.removeEventListener('pointermove', onPointerMove);
    capturedEl.removeEventListener('pointerup', onPointerUp);
    capturedEl.removeEventListener('pointercancel', onPointerUp);
    capturedEl = null;
  }

  activePointerId = null;
  isDragging.value = false;

  if (didDrag) {
    // Swallow the synthetic click that some browsers fire right after a drag.
    suppressClick = true;
    // Fallback clear: on touch (iOS) that click never arrives, so drop the
    // flag shortly after in case no new pointerdown resets it first.
    if (suppressClickTimer !== null) clearTimeout(suppressClickTimer);
    suppressClickTimer = setTimeout(() => {
      suppressClick = false;
      didDrag = false;
      suppressClickTimer = null;
    }, 400);
    // Final safety net: never leave any part off-screen.
    ensureInViewport();
    if (draggedPosition.value) savePosition(draggedPosition.value);
  }
}

function onButtonClick() {
  if (suppressClick || didDrag) {
    if (suppressClickTimer !== null) {
      clearTimeout(suppressClickTimer);
      suppressClickTimer = null;
    }
    suppressClick = false;
    didDrag = false;
    return;
  }
  goToAgent();
}

function onWindowResize() {
  // Viewport shrank/rotated: pull the button back to the nearest side if any
  // part now falls outside, then persist the corrected position.
  if (ensureInViewport() && draggedPosition.value) {
    savePosition(draggedPosition.value);
  }
}

onMounted(() => {
  // Re-clamp the restored position against the current viewport. Wait a frame
  // so the element is measured before we correct it.
  if (draggedPosition.value) {
    requestAnimationFrame(() => {
      if (ensureInViewport() && draggedPosition.value) {
        savePosition(draggedPosition.value);
      }
    });
  }
  window.addEventListener('resize', onWindowResize);
  window.addEventListener('orientationchange', onWindowResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', onWindowResize);
  window.removeEventListener('orientationchange', onWindowResize);
  if (suppressClickTimer !== null) {
    clearTimeout(suppressClickTimer);
    suppressClickTimer = null;
  }
  if (capturedEl) {
    capturedEl.removeEventListener('pointermove', onPointerMove);
    capturedEl.removeEventListener('pointerup', onPointerUp);
    capturedEl.removeEventListener('pointercancel', onPointerUp);
    capturedEl = null;
  }
});
</script>

<style scoped>
.agentic-btn-launcher {
  position: fixed;
  z-index: 40;
  left: 1rem;
  bottom: calc(env(safe-area-inset-bottom) + 0.5rem);
  pointer-events: none;
  transition: bottom 0.3s ease;
  touch-action: none;
}

@media (prefers-reduced-motion: reduce) {
  .agentic-btn-launcher {
    transition: none;
  }
}

/* Once dragged, animate viewport corrections (snap to nearest side). */
.agentic-btn-launcher--dragged {
  transition:
    left 0.25s ease,
    top 0.25s ease;
}

/* While actively dragging, track the pointer 1:1 without easing. */
.agentic-btn-launcher--dragging {
  transition: none;
}

@media (prefers-reduced-motion: reduce) {
  .agentic-btn-launcher--dragged {
    transition: none;
  }
}

.agentic-btn-launcher :deep(.melissa-btn) {
  pointer-events: auto;
  cursor: grab;
  touch-action: none;
}

.agentic-btn-launcher--dragging :deep(.melissa-btn) {
  cursor: grabbing;
}

.agentic-btn-launcher--above-footer {
  bottom: calc(env(safe-area-inset-bottom) + 7rem);
}

@media (min-width: 768px) {
  .agentic-btn-launcher {
    left: 2rem;
  }
  .agentic-btn-launcher--above-footer {
    bottom: calc(env(safe-area-inset-bottom) + 5rem);
  }
}
</style>
